import { GridColDef } from '@mui/x-data-grid-pro';
import {
  getCalendarIndexLogs,
  indexAllCalendars,
  UserKeys,
} from '../../../api/users-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useEffect, useMemo, useState } from 'react';
import { useUserSettings } from '../../../utils/use-user-settings';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import SharedTabHeader from '../../../shared/shared-tab-header';
import EventBusy from '@mui/icons-material/EventBusy';
import Refresh from '@mui/icons-material/Refresh';

export default function CalendarsIndexingGrid() {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar, calendarIndexingIsDisabled } = useStore();
  const [fakeLoading, setFakeLoading] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const { administrationRoles, changeLogRoles } = useUserSettings();

  useEffect(() => {
    if (fakeLoading) {
      setTimeout(() => {
        setFakeLoading(false);
      }, 3000);
    }
  }, [fakeLoading]);

  useEffect(() => {
    if (!calendarIndexingIsDisabled) {
      setFakeLoading(false);
    }
  }, [calendarIndexingIsDisabled]);

  const {
    data: indexCalendarLogs,
    isLoading: isIndexCalendarLogsLoading,
    isFetching: isIndexCalendarLogsFetching,
  } = useQuery({
    queryKey: [UserKeys.IndexCalendarLog],
    queryFn: () => getCalendarIndexLogs(),
    meta: {
      errorMessage:
        'There was an issue loading Calendars Index Log. Please refresh and try again.',
    },
  });

  const mostRecentRunTime = indexCalendarLogs?.reduce((latest, log) => {
    const logTime = new Date(log.lastRunTime);
    return logTime > latest ? logTime : latest;
  }, new Date(0));

  const { mutate, isPending } = useMutation({
    mutationFn: () => indexAllCalendars(changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Indexing for calendars is now running.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the calendars. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.indexing.index_all_calendars, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunCalendarIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setFakeLoading(true);
      mutate();
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lastRunTime',
        headerName: 'Run Time',
        flex: 1,
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'manual',
        headerName: 'Manual Run',
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'emailDomain',
        headerName: 'Email Domain',
        flex: 1,
      },
    ],
    []
  );

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter noQuickFilter>
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          Last Run Time:{' '}
          {mostRecentRunTime
            ? mostRecentRunTime.toLocaleString()
            : 'No runs yet'}
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.IndexCalendarLog],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </SharedToolbar>
    );
  }

  const disabledByRole = !isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.RunCalendarIndexing.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Calendars" />
      <Box sx={{ px: 1 }}>
        <Stack gap={2} mb={2}>
          <Stack flexDirection="row" alignItems="center">
            <Tooltip
              title={
                disabledByRole
                  ? `You need the permission "${AdministrationRoles.RunCalendarIndexing.name}" to perform this action.`
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  loading={fakeLoading || calendarIndexingIsDisabled}
                  disabled={
                    disabledByRole || fakeLoading || calendarIndexingIsDisabled
                  }
                  onClick={handleSubmit}
                >
                  Index All
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ height: '50vh', mx: 'auto' }}>
          <SharedDataGridClient
            columns={columns}
            rows={indexCalendarLogs || []}
            slots={{
              toolbar: CustomToolbar,
              ...createGridOverlays('Calendar Indexing Logs', <EventBusy />),
            }}
            getRowId={(row) => row.lastRunTime}
            loading={isIndexCalendarLogsLoading || isIndexCalendarLogsFetching}
          />
        </Box>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate();
        }}
        mutateLoading={isPending}
      />
    </Stack>
  );
}
